import { useTranslation } from 'react-i18next';

import { ArrowBox } from '~/components/common/arrow-box';
import { CachedImage } from '~/components/common/cached-image/cached-image';
import { cn } from '~/utils/cn';

/** Common shape across the membership-drill-down product rows (term,
 *  occasion, attribute, trend) — a structural subset of
 *  `InsightMembershipProductRowWire`. */
export interface ProductRowCardData {
  productId: string;
  externalId: string | null;
  title: string | null;
  imageLink: string | null;
  itemGroupId: string | null;
  variantCount: number | null;
  appearsIn: readonly string[] | null;
  contentQualityBefore: number | null;
  contentQualityAfter: number | null;
}

interface ProductRowCardProps {
  product: ProductRowCardData;
  /** i18n key prefix under the `catalog-operations` namespace for this call
   *  site's copy (e.g. `'termDrawer'`, `'membershipDrawer'`) — lets each
   *  drawer own its wording while sharing this one row renderer. Expects
   *  `{prefix}.product.{parent,standalone,variants,contentQuality}` and
   *  `{prefix}.appearsIn.{title,description,highlights,attribute}`. */
  i18nPrefix: string;
  /** Adds the hover affordance for a card the caller wraps in a link — the
   *  card itself stays a plain block so the caller owns the route typing. */
  interactive?: boolean;
  /** `data-testid` root (default `product-row-card-{productId}`); derives `-title`, `-id`, `-quality-before`, `-quality-after`. */
  testId?: string;
}

/**
 * One product row shared by every insight-membership drawer: thumbnail,
 * title/id, parent/variant info, appearsIn pills (omitted entirely when
 * `appearsIn` is `null` or empty — that's a normal row, not a gap), and the
 * enrichment agent's own before→after content-quality self-grade.
 */
export function ProductRowCard({
  product,
  i18nPrefix,
  interactive = false,
  testId = `product-row-card-${product.productId}`,
}: ProductRowCardProps) {
  const { t } = useTranslation('catalog-operations');
  const title = product.title ?? product.externalId ?? product.productId;

  return (
    <div
      data-testid={testId}
      className={cn(
        'bg-background flex gap-3 rounded-lg border p-3',
        interactive && 'hover:border-primary/40 cursor-pointer transition-colors hover:shadow-sm',
      )}
    >
      <CachedImage src={product.imageLink} alt="" className="size-11 shrink-0 rounded-md" />

      <div className="min-w-0 flex-1 space-y-1.5">
        <p data-testid={`${testId}-title`} className="text-foreground truncate text-sm font-medium">
          {title}
        </p>
        <p data-testid={`${testId}-id`} className="text-muted-foreground truncate text-xs">
          {product.externalId ?? product.productId}
          {' · '}
          {product.itemGroupId
            ? t(`${i18nPrefix}.product.parent`, { id: product.itemGroupId })
            : t(`${i18nPrefix}.product.standalone`)}
          {product.variantCount != null && product.variantCount > 1
            ? ` · ${t(`${i18nPrefix}.product.variants`, { count: product.variantCount })}`
            : ''}
        </p>

        {product.appearsIn && product.appearsIn.length > 0 && (
          <div className="flex flex-wrap gap-1">
            {product.appearsIn.map((where) => (
              <span
                key={where}
                data-testid={`${testId}-appears-in-${where.toLowerCase()}`}
                className="bg-muted text-muted-foreground rounded px-1.5 py-0.5 text-[11px] font-medium"
              >
                {t(`${i18nPrefix}.appearsIn.${where.toLowerCase()}`)}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 pt-0.5">
          <span className="text-muted-foreground text-[11px]">
            {t(`${i18nPrefix}.product.contentQuality`)}
          </span>
          <span
            data-testid={`${testId}-quality-before`}
            className="text-warning text-xs font-semibold tabular-nums"
          >
            {product.contentQualityBefore ?? '—'}
          </span>
          <ArrowBox />
          <span
            data-testid={`${testId}-quality-after`}
            className="text-primary text-xs font-semibold tabular-nums"
          >
            {product.contentQualityAfter ?? '—'}
          </span>
        </div>
      </div>
    </div>
  );
}
